/**
 * spaceShardSystem.ts
 * Pool + spawn + collision pipeline for World 2 Constellation Shards.
 *
 * Mirror of `homingMineSpawner.ts` shape (pool-managed entity list,
 * scene-pushed via `spawn`, reaped each tick), but with two differences:
 *
 *  1. Shards are level-config-driven, not Poisson-spawned. Each W2 main
 *     level (2-1 .. 2-4) declares 3 shards at fixed world-space
 *     coordinates; `SpaceScene.init()` calls {@link spawnShardsFromLevel}
 *     once on level entry, no per-tick spawner runs.
 *  2. Shards are collected by SHIP-vs-SHARD AABB (not consumed by
 *     projectiles or absorbed into damage pools). Collection ids
 *     accumulate in a per-session array exposed via
 *     {@link getCollectedShardIdsThisSession} so
 *     {@link ../levels/spaceLevelSession.persistSpaceLevelCompletion}
 *     can merge them into the save on level clear.
 *
 * NO React imports, NO Skia imports, NO async — engine-internal only.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module spaceShardSystem
 */

import { ObjectPool } from '../utils/objectPool';
import { SpaceShard } from '../entities/SpaceShard';
import { spaceState } from './spaceState';
import { SPACE_SHARD_POOL_CAP } from '../../constants/spaceShardConstants';
import type { SpaceLevelConfig } from '../levels/spaceLevelTypes';
import type { AABB } from '../types/entity';
import type { LevelRuntime } from './gameState';

const EMPTY_SPACE_LEVEL: LevelRuntime = { width: 0, height: 0, platforms: [] };

const shardPool = new ObjectPool<SpaceShard>(
  () => new SpaceShard(),
  SPACE_SHARD_POOL_CAP,
);
shardPool.prewarm(SPACE_SHARD_POOL_CAP);

/**
 * Per-session list of shard ids collected since the last
 * {@link resetSpaceShards} call. Populated by
 * {@link resolveShipShardCollisions} on each AABB hit, drained by
 * `persistSpaceLevelCompletion()` at level clear via
 * {@link getCollectedShardIdsThisSession}. Module-local so
 * `spaceState` stays at one new field (`shards`) instead of two.
 */
const collectedShardIdsThisSession: string[] = [];

/**
 * Pool-availability accessor for tests. Returns the number of pooled
 * shards not currently in use.
 *
 * @returns Slots available in the shard pool.
 */
export function _spaceShardPoolAvailable(): number {
  return shardPool.available;
}

/**
 * Read-only view of the shard ids collected during the active session.
 * `persistSpaceLevelCompletion()` reads this at level clear so it can
 * merge them into the save's
 * `levels[levelId].collectedConstellationShardIds` array.
 *
 * @returns A snapshot copy of the collected-id array.
 */
export function getCollectedShardIdsThisSession(): string[] {
  return collectedShardIdsThisSession.slice();
}

/**
 * Release every active shard back to the pool, clear the scene's
 * `spaceState.shards` array, and drain the per-session
 * collected-id list. Called by `SpaceScene.init()` /
 * `SpaceScene.destroy()` so a session restart cannot inherit shards
 * (or collected ids) from the prior run.
 */
export function resetSpaceShards(): void {
  const live = spaceState.shards;
  for (let i = live.length - 1; i >= 0; i--) {
    shardPool.release(live[i]);
  }
  live.length = 0;
  collectedShardIdsThisSession.length = 0;
}

/**
 * Spawn every shard declared in `config.shards` (no-op when undefined).
 * Each entry is acquired from the pool, pushed into
 * `spaceState.shards`, and tagged with the entry's `id`.
 *
 * Pool exhaustion silently drops the surplus — the level designer is
 * expected to keep shard counts within {@link SPACE_SHARD_POOL_CAP}.
 *
 * @param config - Active level config (read its `shards` field).
 */
export function spawnShardsFromLevel(config: SpaceLevelConfig): void {
  const shards = config.shards;
  if (shards === undefined) return;
  for (const entry of shards) {
    const shard = shardPool.acquire();
    if (shard === null) break;
    shard.spawn(entry.id, entry.x, entry.y);
    spaceState.shards.push(shard);
  }
}

/**
 * Advance every active shard one tick and reap any that drifted past
 * the camera's left cull margin.
 *
 * @param dt - Fixed timestep in seconds.
 */
export function updateSpaceShards(dt: number): void {
  for (let i = spaceState.shards.length - 1; i >= 0; i--) {
    const s = spaceState.shards[i];
    s.update(dt, EMPTY_SPACE_LEVEL);
    if (s.dead) {
      shardPool.release(s);
      spaceState.shards.splice(i, 1);
    }
  }
}

function overlaps(a: AABB, b: AABB): boolean {
  return !(
    a.x + a.width <= b.x ||
    a.x >= b.x + b.width ||
    a.y + a.height <= b.y ||
    a.y >= b.y + b.height
  );
}

/**
 * Pairwise AABB collision between the ship and live shards. On a hit
 * the shard's id is appended to {@link collectedShardIdsThisSession}
 * and the shard is reaped (released back to the pool next tick via
 * the `dead` flag). No damage, no iframes, no VFX — pickups are silent
 * in this slice; audio + sparkle bursts land in a follow-up.
 *
 * Skipped entirely when `spaceState.ship` is null (scene not
 * initialised) or `spaceState.gameOver` is true (mirror of the
 * hazard-skip rule in `resolveShipHazardCollisions`).
 */
export function resolveShipShardCollisions(): void {
  const ship = spaceState.ship;
  if (ship === null) return;
  if (spaceState.gameOver) return;
  const shipBox = ship.getHitbox();
  const shards = spaceState.shards;
  for (let i = 0; i < shards.length; i++) {
    const s = shards[i];
    if (s.dead || s.collected) continue;
    if (!overlaps(shipBox, s.getHitbox())) continue;
    s.collected = true;
    s.dead = true;
    s.active = false;
    collectedShardIdsThisSession.push(s.shardId);
  }
}
